import { CinematicSequenceSchema } from '../../../renderer/src/cinematic-sequence-contract';
import type { CinematicSequence, CinematicWaypoint } from '../../../renderer/src/cinematic-sequence';
import { compileRideProgress, type RidePoint } from './ride';
import { filmTraversalBounds, journeyProgressAtFilm } from './camera-plan';

export interface TimedCinematicWaypoint {
  waypoint: CinematicWaypoint;
  journeyProgress: number;
  seconds: number;
  effectiveDurationSeconds: number;
  transitionStartSeconds: number;
}

/** Validates anchors against full imported samples and maps them onto film time. */
export function cinematicTimeline(points: readonly RidePoint[], candidate: CinematicSequence, duration: number): TimedCinematicWaypoint[] {
  const sequence = CinematicSequenceSchema.parse(candidate);
  const route = compileRideProgress(points);
  const { startSeconds, endSeconds } = filmTraversalBounds(duration);
  const timed = sequence.waypoints.map(waypoint => {
    const point = points[waypoint.anchor.pointIndex];
    if (!point || Date.parse(point.recordedAt) !== Date.parse(waypoint.anchor.recordedAt)) {
      throw new Error('A cinematic keyframe no longer matches its original trace sample. Remove or reposition it.');
    }
    const journeyProgress = route.distanceFractions[waypoint.anchor.pointIndex]!;
    return { waypoint, journeyProgress, seconds: waypoint.filmProgress === undefined
      ? startSeconds + journeyProgress * (endSeconds - startSeconds) : waypoint.filmProgress * duration,
    effectiveDurationSeconds: 0, transitionStartSeconds: 0 };
  }).sort((a, b) => a.seconds - b.seconds);
  for (const [index, item] of timed.entries()) {
    const properties = Object.keys(item.waypoint.overrides);
    if (timed.slice(0, index).some(previous => Math.abs(item.seconds - previous.seconds) < 1e-9
      && (!properties.length || !Object.keys(previous.waypoint.overrides).length || properties.some(property => property in previous.waypoint.overrides)))) {
      throw new Error('Only one cinematic keyframe can occupy a playback position.');
    }
    const next = timed[index + 1]?.seconds ?? (item.waypoint.filmProgress === undefined ? endSeconds : duration);
    const arrival = item.waypoint.transition.timing === 'arrival';
    const gap = arrival ? item.seconds - (timed[index - 1]?.seconds ?? 0) : next - item.seconds;
    item.effectiveDurationSeconds = item.waypoint.transition.type === 'cut' ? 0
      : Math.max(0, Math.min(item.waypoint.transition.entireInterval ? gap : item.waypoint.transition.durationSeconds, gap));
    item.transitionStartSeconds = arrival ? item.seconds - item.effectiveDurationSeconds : item.seconds;
  }
  return timed;
}

/** Selects an original sample without modifying recorded coordinates or timestamps. */
export function cinematicAnchorAt(points: readonly RidePoint[], filmProgress: number, duration: number): CinematicWaypoint['anchor'] {
  const fractions = compileRideProgress(points).distanceFractions;
  const target = journeyProgressAtFilm(filmProgress, duration);
  let left = 0; let right = fractions.length - 1;
  while (left < right) {
    const middle = Math.floor((left + right) / 2);
    if (fractions[middle]! < target) left = middle + 1; else right = middle;
  }
  let index = left > 0 && target - fractions[left - 1]! < fractions[left]! - target ? left - 1 : left;
  // Interior distance plateaus display the following segment in the ride evaluator.
  if (target > 0) while (index + 1 < fractions.length && fractions[index + 1] === fractions[index]) index++;
  return { pointIndex: index, recordedAt: points[index]!.recordedAt };
}
